import React, { useState } from 'react';

import Hero from '../components/Hero';
import BrandMarkTransition from './components/BrandMarkTransition';
import DownloadFinale from './components/DownloadFinale';
import FocusChapter from './components/FocusChapter';
import GuidedScroll from './components/GuidedScroll';
import LanguageBridge from './components/LanguageBridge';
import LifeChapterPreview from './components/LifeChapterPreview';
import ReflectionPreview from './components/ReflectionPreview';
import RelationshipRules from './components/RelationshipRules';
import ResonanceTease from './components/ResonanceTease';
import TastePreview from './components/TastePreview';
import WindowChapter from './components/WindowChapter';
import ComparisonMatrix from './components/ComparisonMatrix';

/**
 * Ordered chapter indices that use Milan's dark visual treatment. This frozen
 * collection takes no inputs, returns shared presentation data, and has no
 * side effects.
 */
export const DARK_CHAPTER_INDICES = Object.freeze([4, 8, 9, 10]);

/**
 * Determines whether a guided chapter uses the dark visual treatment.
 *
 * @param {number} chapterIndex - Active guided chapter index.
 * @returns {boolean} Whether the index is a configured dark chapter.
 * @sideEffects None.
 */
export function isDarkChapter(chapterIndex) {
  return DARK_CHAPTER_INDICES.includes(chapterIndex);
}

/**
 * Composes Milan's ordered guided marketing experience. It accepts no inputs
 * and returns the persistent brand treatment with all nine narrative chapters.
 * The active chapter is stored locally and updated as GuidedScroll reports the
 * reader's position, which changes the root data attribute and brand treatment.
 *
 * @returns {React.ReactElement} The complete guided landing-page experience.
 * @sideEffects Updates local scroll-derived presentation state.
 */
export default function GuidedLandingPage() {
  const [activeChapter, setActiveChapter] = useState(0);

  return (
    <div className="guided-experience" data-active-chapter={activeChapter}>
      <GuidedScroll onIndexChange={setActiveChapter}>
        <BrandMarkTransition
          compact={activeChapter > 0}
          onDark={isDarkChapter(activeChapter)}
        />
        <Hero />
        <RelationshipRules index={1} />
        <WindowChapter index={2} />
        <ReflectionPreview index={3} isActive={activeChapter === 3} />
        <FocusChapter index={4} />
        <TastePreview index={5} isActive={activeChapter === 5} />
        <LifeChapterPreview index={6} isActive={activeChapter === 6} />
        <LanguageBridge index={7} />
        <ResonanceTease index={8} />
        <ComparisonMatrix index={9} />
        <DownloadFinale index={10} />
      </GuidedScroll>
    </div>
  );
}
